import React, { useState, useEffect, useCallback } from 'react';
import { getPickemData, savePickemPicks } from '../../services/api';

export default function PickemModal({ tournamentId }) {
    const [data, setData] = useState(null);
    const [picks, setPicks] = useState({ threeZero: [], zeroThree: [], advance: [] });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [timeLeft, setTimeLeft] = useState('');

    // Загрузка данных
    const loadData = useCallback(() => {
        getPickemData(tournamentId).then(res => {
            setData(res.data);
            if (res.data.userPicks) setPicks(res.data.userPicks);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [tournamentId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Таймер обратного отсчета
    useEffect(() => {
        if (!data?.deadline || data.isClosed) return;

        const updateTimer = () => {
            const now = new Date();
            const end = new Date(data.deadline);
            const diff = end - now;

            if (diff <= 0) {
                setTimeLeft('ЗАКРЫТО');
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24).toString().padStart(2, '0');
            const mins = Math.floor((diff / 1000 / 60) % 60).toString().padStart(2, '0');
            const secs = Math.floor((diff / 1000) % 60).toString().padStart(2, '0');

            setTimeLeft(days > 0 ? `${days}д ${hours}:${mins}:${secs}` : `${hours}:${mins}:${secs}`);
        };

        const timerId = setInterval(updateTimer, 1000);
        updateTimer();
        return () => clearInterval(timerId);
    }, [data]);

    const isClosed = data?.isClosed;

    // --- DRAG N DROP LOGIC ---

    const onDragStart = (e, teamId) => {
        if (isClosed) return;
        e.dataTransfer.setData("teamId", teamId);
    };

    const onDragOver = (e) => e.preventDefault();

    const onDropToSlot = (e, category, index) => {
        e.preventDefault();
        if (isClosed) return;

        const teamId = e.dataTransfer.getData("teamId");
        
        // Очищаем команду из всех категорий (перемещение)
        const newPicks = {
            threeZero: picks.threeZero.filter(id => id !== teamId),
            zeroThree: picks.zeroThree.filter(id => id !== teamId),
            advance: picks.advance.filter(id => id !== teamId),
        };

        const currentCategoryPicks = [...newPicks[category]];
        currentCategoryPicks[index] = teamId;
        newPicks[category] = currentCategoryPicks;

        setPicks(newPicks);
    };

    const onDropToPool = (e) => {
        e.preventDefault();
        if (isClosed) return;
        const teamId = e.dataTransfer.getData("teamId");
        removeTeamFromPicks(teamId);
    };

    const removeTeamFromPicks = (teamId) => {
        if (isClosed) return;
        setPicks({
            threeZero: picks.threeZero.filter(id => id !== teamId),
            zeroThree: picks.zeroThree.filter(id => id !== teamId),
            advance: picks.advance.filter(id => id !== teamId),
        });
    };

    const isTeamPicked = (id) => [
        ...picks.threeZero, ...picks.zeroThree, ...picks.advance
    ].includes(id);

    const handleSave = async () => {
        setSaving(true);
        try {
            await savePickemPicks(tournamentId, picks);
            alert('Прогнозы сохранены!');
        } catch (e) {
            alert('Ошибка при сохранении');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="screen-loader">Загрузка Пикема...</div>;

    const renderSlot = (category, index) => {
        const teamId = picks[category][index];
        const team = data.teams.find(t => t.id === teamId);
        const stats = data.teamStats ? data.teamStats[teamId] : null;
        
        const isCorrect = team && (
            (category === 'threeZero' && stats?.finalResult === '3-0') ||
            (category === 'zeroThree' && stats?.finalResult === '0-3') ||
            (category === 'advance' && (stats?.finalResult === 'adv'))
        );
    const isWrong = team && isClosed && (
        (category === 'threeZero' && stats?.losses > 0) ||
        (category === 'zeroThree' && stats?.wins > 0) ||
        (category === 'advance' && (stats?.losses > 2 || (stats?.wins === 3 && stats?.losses === 0)))
    );
        return (
            <div 
                className={`pickem-slot ${isCorrect ? 'correct' : ''} ${isWrong ? 'wrong' : ''}`}
                onDragOver={onDragOver}
                onDrop={(e) => onDropToSlot(e, category, index)}
                onClick={() => team && removeTeamFromPicks(teamId)}
            >
                {team ? (
                    <>
                        <img src={team.logoUrl} alt={team.name} draggable={!isClosed} onDragStart={(e) => onDragStart(e, team.id)} />
                        {isCorrect && <span className="pick-result success">+100 </span>}
                        {isWrong && <span className="pick-result error">0</span>}
                    </>
                ) : <span className="slot-empty">?</span>}
            </div>
        );
    };

    return (
        <div className="pickem-container">
            <div className="pickem-header-main">
                <h2>Pick'Em Challenge</h2>
                <div className="pickem-timer">
                    {isClosed ? <span>🔒 ПРИЕМ ЗАКРЫТ</span> : <span>⏳ {timeLeft}</span>}
                </div>
            </div>

            {!isClosed && (
                <div className="pickem-teams-pool" onDragOver={onDragOver} onDrop={onDropToPool}>
                    {data.teams.filter(t => !isTeamPicked(t.id)).map(team => (
                        <div key={team.id} className="pool-team" draggable onDragStart={(e) => onDragStart(e, team.id)}>
                            <img src={team.logoUrl} alt={team.name} />
                            <span>{team.name}</span>
                        </div>
                    ))}
                    {data.teams.filter(t => !isTeamPicked(t.id)).length === 0 && <p className="all-picked-msg">Все расставлено!</p>}
                </div>
            )}

            <div className="pickem-grid">
                <div className="pickem-column">
                    <div className="pickem-cat-box">
                        <span className="cat-title">3 - 0 (Advanced)</span>
                        <div className="slots-row">
                            {[0, 1].map(i => renderSlot('threeZero', i))}
                        </div>
                    </div>
                    <div className="pickem-cat-box">
                        <span className="cat-title">0 - 3 (Eliminated)</span>
                        <div className="slots-row">
                            {[0, 1].map(i => renderSlot('zeroThree', i))}
                        </div>
                    </div>
                </div>

                <div className="pickem-cat-box advance">
                    <span className="cat-title">3-1 / 3-2 (To Advance)</span>
                    <div className="slots-grid-6">
                        {[0, 1, 2, 3, 4, 5].map(i => renderSlot('advance', i))}
                    </div>
                </div>
            </div>

            {!isClosed && (
                <button className="confirm-transfer-btn" style={{width: '100%'}} onClick={handleSave} disabled={saving}>
                    {saving ? 'Сохранение...' : 'Сохранить прогнозы'}
                </button>
            )}
        </div>
    );
}